// stores/maintenanceStore.ts
import { createJSONStorage, persist } from 'zustand/middleware'

import { isAxiosError } from 'axios'

import { create } from 'zustand'

import apiClient from '@/api/axios'

import { decryptData, encryptData } from '@/lib/crypto'
import { logger } from '@/lib/logger'

import toast from '@/components/toast'

import i18n from '@/i18n'

interface MaintenanceState {
  isMaintenance: boolean
  message: string
  retryAfter: number | null
  lastChecked: Date | null
  checkMaintenance: () => Promise<boolean>
  startPolling: (interval?: number) => () => void
  forceRecovery: () => void
  set: (state: Partial<MaintenanceState>) => void
}

export const useMaintenanceStore = create<MaintenanceState>()(
  persist(
    (set, get) => ({
      isMaintenance: false,
      message: '',
      retryAfter: null,
      lastChecked: null,

      checkMaintenance: async () => {
        try {
          // Use a simple health check endpoint
          const response = await apiClient.get('/api/status', { isRoot: true })
          if (response.status === 503) {
            const { message = 'Under maintenance', retry_after = null } = response.data?.data || {}
            set({
              isMaintenance: true,
              message,
              retryAfter: retry_after,
              lastChecked: new Date(),
            })
            return true
          }

          // If we were in maintenance but now it's resolved
          if (get().isMaintenance) {
            toast.success(i18n.t('common:maintenance.completed', 'Maintenance completed. Application refreshed.'))
            logger.warn('Maintenance mode down')
            setTimeout(() => window.location.reload(), 1500)
          }

          set({
            isMaintenance: false,
            lastChecked: new Date(),
          })

          return false
        } catch (error: unknown) {
          // Only set maintenance if we get a 503
          if (isAxiosError(error) && error.response?.status === 503) {
            const maintenanceData = error.response?.data as any
            set({
              isMaintenance: true,
              message: maintenanceData?.message || 'Service unavailable',
              retryAfter: maintenanceData?.retry_after ? parseInt(String(maintenanceData.retry_after), 10) : null,
              lastChecked: new Date(),
            })

            logger.warn('Maintenance mode detected', error)

            return true
          }

          return get().isMaintenance // Maintain current state
        }
      },

      startPolling: (interval = 10000) => {
        const check = () => get().checkMaintenance()

        // Initial check
        check()

        // Set up interval
        const pollInterval = setInterval(check, interval)

        // Return cleanup function
        return () => clearInterval(pollInterval)
      },

      forceRecovery: () => {
        set({
          isMaintenance: false,
          message: '',
          retryAfter: null,
        })
        toast.warning(i18n.t('common:maintenance.bypassed', 'You have bypassed maintenance mode'))
      },

      set: (state) => set(state),
    }),
    {
      name: 'bbg-mtn',
      storage: createJSONStorage(() => ({
        getItem: (name) => {
          const encrypted = localStorage.getItem(name)
          if (!encrypted) return null
          try {
            const decrypted = decryptData(encrypted)
            return decrypted
          } catch (error) {
            // Silently remove corrupted data
            localStorage.removeItem(name)
            return null
          }
        },
        setItem: (name, value) => {
          try {
            const encrypted = encryptData(value)
            localStorage.setItem(name, encrypted)
          } catch (error) {
            logger.error('Failed to encrypt maintenance data:', error)
            localStorage.setItem(name, value)
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      })),
      partialize: (state) => ({
        isMaintenance: state.isMaintenance,
        message: state.message,
        retryAfter: state.retryAfter,
        lastChecked: state.lastChecked,
      }),
    }
  )
)
